import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useApp, useUI } from "@/context/AppContext";
import { resolveMedia } from "@/lib/api";
import { SmartImage } from "@/components/SmartImage";

const Brand = ({ onClick }) => {
  const { m, loaded } = useApp();
  const logo = m("logo");
  const logoClass = "h-[76px] w-auto max-w-[150px] object-contain sm:h-[88px] sm:max-w-[200px] md:h-[108px] md:max-w-[260px]";

  return (
    <Link to="/" onClick={onClick} data-testid="brand-logo" className="flex items-center gap-3">
      {!loaded ? (
        // skeleton while site content/media is still resolving — avoids the
        // text-name flashing before the real logo image is known
        <div className={`${logoClass} animate-pulse rounded-lg bg-neutral-200`} aria-hidden="true" />
      ) : logo ? (
        <SmartImage
          src={resolveMedia(logo)}
          alt="Agrivoire Export SARL"
          className={logoClass}
          imgClassName="h-full w-full object-contain"
        />
      ) : (
        <span className="font-montserrat text-2xl font-black leading-none tracking-tight sm:text-3xl md:text-4xl">
          <span className="text-[#F5821F]">Agrivoire</span>{" "}
          <span className="text-[#1CADE4]">Export</span>
        </span>
      )}
    </Link>
  );
};

export default function Header() {
  const { t, lang, toggleLang } = useApp();
  const ui = useUI();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: t("global", "nav_home"), id: "home" },
    { to: "/courtage", label: t("global", "nav_courtage"), id: "courtage" },
    { to: "/conseil", label: t("global", "nav_conseil"), id: "conseil" },
    { to: "/a-propos", label: t("global", "nav_apropos"), id: "apropos" },
    { to: "/contact", label: t("global", "nav_contact"), id: "contact" },
  ];

  const linkClass = ({ isActive }) =>
    `relative font-poppins text-sm font-medium tracking-wide transition-colors duration-300 ${
      isActive ? "text-[#4E7D2C]" : "text-neutral-700 hover:text-[#4E7D2C]"
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-black/5 bg-white/85 backdrop-blur-xl" : "bg-white/60 backdrop-blur-md"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[96px] max-w-[1400px] items-center justify-between px-5 md:h-[132px] md:px-10">
        <Brand />

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {links.map((l) => (
            <NavLink key={l.id} to={l.to} end={l.to === "/"} className={linkClass} data-testid={`nav-${l.id}-link`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleLang}
            data-testid="lang-switcher"
            className="hidden items-center gap-1.5 rounded-full border border-black/10 px-3 py-1.5 font-poppins text-xs font-semibold text-neutral-700 transition-colors hover:border-[#1CADE4] hover:text-[#1CADE4] sm:flex"
          >
            <Globe className="h-3.5 w-3.5" />
            {ui.lang_switch}
          </button>
          <Link
            to="/contact#buyer-form"
            data-testid="header-cta-offer"
            className="hidden rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C] px-5 py-2.5 font-poppins text-sm font-semibold text-white shadow-sm transition-transform duration-300 hover:scale-105 sm:inline-block"
          >
            {t("global", "cta_offer")}
          </Link>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="flex h-10 w-10 items-center justify-center rounded-full text-neutral-800 lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-black/5 bg-white lg:hidden" data-testid="mobile-nav">
          <div className="flex flex-col gap-1 px-5 py-4">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                end={l.to === "/"}
                className="rounded-lg px-3 py-3 font-poppins text-base font-medium text-neutral-800 hover:bg-neutral-50"
                data-testid={`mobile-nav-${l.id}-link`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-2 flex items-center gap-3">
              <button
                onClick={toggleLang}
                data-testid="mobile-lang-switcher"
                className="flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2.5 font-poppins text-sm font-semibold"
              >
                <Globe className="h-4 w-4" /> {ui.lang_switch}
              </button>
              <Link
                to="/contact#buyer-form"
                data-testid="mobile-cta-offer"
                className="flex-1 rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C] px-5 py-2.5 text-center font-poppins text-sm font-semibold text-white"
              >
                {t("global", "cta_offer")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
